import { useMemo } from "react";
import { useAppSelector } from "@store";
import { EncodingVersionDto } from "app/SharedApiClient";
import { maxStringBy } from "utils/arrayHelper";
import { CompareVersionValidationError, CompareVersionValidationResult } from "./types";

export const useCompareVersionValidation = (
  referenceVersion?: EncodingVersionDto,
  comparatorVersion?: EncodingVersionDto,
) => {
  const { referenceEncodingVersions } = useAppSelector((state) => state.compareVersions);
  const latestReferenceVersionId = useMemo(
    () => maxStringBy(referenceEncodingVersions, (version) => version.versionId),
    [referenceEncodingVersions],
  );

  return useMemo(() => {
    const result: CompareVersionValidationResult[] = [];

    if (!referenceVersion) {
      return result;
    }

    if (referenceVersion.versionId !== latestReferenceVersionId) {
      {
        result.push({
          type: CompareVersionValidationError.NotLatestVersionAsReference,
          severity: "warning",
          text: "You are NOT using the latest version as your reference file. Proceed with caution.",
        });
      }
    }

    if (!comparatorVersion) {
      return result;
    }

    if (referenceVersion.projectId !== comparatorVersion.projectId) {
      result.push({
        type: CompareVersionValidationError.DifferentProjects,
        severity: "error",
        text: "You are comparing two different datasets. Consult with your IPS representative before proceeding.",
      });
    }

    if (
      referenceVersion.versionId === comparatorVersion.versionId &&
      referenceVersion.projectId === comparatorVersion.projectId
    ) {
      result.push({
        type: CompareVersionValidationError.SameVersions,
        severity: "error",
        text: "The two versions you have selected are the same.",
      });
    }

    return result;
  }, [referenceVersion, comparatorVersion, latestReferenceVersionId]);
};

export default useCompareVersionValidation;
